import type { AppliedTag } from '@wa/shared';

/**
 * One tag.
 *
 * The colour is the tag's identity, not its entire surface. Painted as a solid
 * fill — which is what this did, because the stored palette is saturated darks and
 * the text was forced to white — six tags in a list read as a row of warnings and
 * out-shout the unread badge, which is the one thing on that row that should
 * shout.
 *
 * The chip now carries the colour as a wash, as its text and as a hairline edge.
 * The mixing happens in CSS against the theme's own surface (see `.tag-chip`), so
 * one stored hex works in both themes without a parallel table of colours.
 */
export function TagChip({ tag, onRemove, size = 'sm' }: {
  tag: AppliedTag;
  onRemove?: () => void;
  size?: 'xs' | 'sm';
}) {
  return (
    <span
      // min-w-0 matters: without it the chip refuses to shrink below its text in a
      // flex row, and a long tag pushes whatever follows it off the edge rather
      // than truncating itself.
      className={`tag-chip inline-flex min-w-0 max-w-full items-center gap-1 rounded-(--radius-chip) font-medium ${
        size === 'xs' ? 'px-1.5 py-px text-micro' : 'px-2 py-0.5 text-micro'
      }`}
      style={{ ['--tag' as string]: tag.colour ?? '#475569' }}
    >
      <span className="min-w-0 truncate">{tag.name}</span>

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove the ${tag.name} tag`}
          className="tap -mr-0.5 grid shrink-0 place-items-center rounded-full opacity-60 transition hover:opacity-100"
        >
          <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </span>
  );
}
